import { useState } from "react";
import {
  LAB_RESULT_CHECK_OUTCOME_LABEL,
  type LabResultCheck,
  type LabResultCheckOutcome,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  labResultCheckChoices,
  labResultCheckDoneText,
  labResultCheckLines,
} from "@/lib/lab-result-check";
import { recordLabResultCheck } from "@/lib/work-queue-api";
import { useReadOnly } from "./SupportView";

const BTN =
  "rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-textPrimary disabled:opacity-50";
const BTN_PRIMARY =
  "rounded-md bg-brand px-md py-sm text-sm text-white hover:bg-brand/90 disabled:opacity-50";

// TES-1284 (TES-1269 point 6) — "Kontrollera provsvar i Webdoc" on the lab
// item itself: her stated sampling time, the latest check, how many there
// have been, and the doctor's three choices. Shown without expanding the
// card, because the choice IS the task. The admin's read-only support view
// sees the facts and no buttons (TES-1040).
export function LabResultCheckPanel({
  patientId,
  orderId,
  check,
  onRecorded,
  onUnauthorized,
}: {
  patientId: string;
  orderId: string;
  check: LabResultCheck;
  onRecorded?: () => void;
  onUnauthorized?: () => void;
}) {
  const readOnly = useReadOnly();
  const [busy, setBusy] = useState<LabResultCheckOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const choices = readOnly ? [] : labResultCheckChoices(check);

  async function choose(outcome: LabResultCheckOutcome) {
    setBusy(outcome);
    setError(null);
    try {
      const res = await recordLabResultCheck(patientId, orderId, outcome);
      setDone(labResultCheckDoneText(outcome, res.check));
      onRecorded?.();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized?.();
      else
        setError(
          errorText(e instanceof Error ? e.message : "") ||
            "Kontrollen kunde inte sparas.",
        );
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-col gap-sm border-t border-surfaceMuted px-md py-sm">
      <ul className="flex flex-wrap gap-x-md gap-y-xs text-xs text-textSecondary">
        {labResultCheckLines(check).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      {done ? (
        <p className="text-sm text-textSecondary">{done}</p>
      ) : choices.length > 0 ? (
        <div className="flex flex-wrap gap-sm">
          {choices.map((outcome, i) => (
            <button
              key={outcome}
              type="button"
              disabled={busy !== null}
              onClick={() => void choose(outcome)}
              className={i === 0 ? BTN_PRIMARY : BTN}
            >
              {LAB_RESULT_CHECK_OUTCOME_LABEL[outcome]}
            </button>
          ))}
        </div>
      ) : null}
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}
